import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LucideIcon } from "lucide-react";
import IndicadorTooltip from "@/components/IndicadorTooltip";

interface KPICardProps {
  label: string;
  value: number | string;
  icon: LucideIcon;
  iconColor?: string;
  iconBg?: string;
  subtitle?: string;
  trend?: { value: number; label: string };
  loading?: boolean;
  onClick?: () => void;
  /** Chave do indicador no glossário para exibir tooltip descritivo */
  tooltipKey?: string;
  /** Descrição customizada para o tooltip (alternativa ao tooltipKey) */
  tooltipDescricao?: string;
  /** Interpretação customizada para o tooltip */
  tooltipInterpretacao?: string;
}

export function KPICard({ label, value, icon: Icon, iconColor = "text-primary", iconBg = "bg-primary/10", subtitle, trend, loading, onClick, tooltipKey, tooltipDescricao, tooltipInterpretacao }: KPICardProps) {
  if (loading) {
    return (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-10 w-10 rounded-lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`p-5 transition-colors ${onClick ? "cursor-pointer hover:bg-muted/50" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
            {(tooltipKey || tooltipDescricao) && (
              <IndicadorTooltip
                indicadorKey={tooltipKey}
                descricao={tooltipDescricao}
                interpretacao={tooltipInterpretacao}
                side="top"
                size={12}
              />
            )}
          </div>
          <p className="text-2xl font-bold tabular-nums">{typeof value === "number" ? value.toLocaleString("pt-BR") : value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          {trend && (
            <p className={`text-xs font-medium ${trend.value >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {trend.value >= 0 ? "+" : ""}{trend.value}% {trend.label}
            </p>
          )}
        </div>
        <div className={`p-2.5 rounded-lg ${iconBg}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
      </div>
    </Card>
  );
}

export function KPICardRow({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {children}
    </div>
  );
}
